import { toPairs } from "ramda";

import { buildIframeURL, removeHeightFromStyle } from ".";

import { TAB_STRING } from "../constants";

const camelCasedAppName = globalProps.appName.replace(/^neeto/i, "neeto");
const embedFunctionName = `embed${globalProps.appName}`;

const embedScript =
  scriptLink => `const embedScript = document.createElement("script");
      embedScript.type = "module";
      embedScript.src = "${scriptLink}";
      embedScript.async = true;
`;

const cleanupFunction = `
      return () => {
        embedScript.onload = null;
        if(window.${camelCasedAppName}) window.${camelCasedAppName}.destroy();
        document.body.removeChild(embedScript);
      };`;

const embedEnv = () =>
  globalProps.railsEnv !== "production"
    ? `\n${TAB_STRING}${TAB_STRING}  env: "${globalProps.railsEnv}",`
    : "";

const embedQueryParam = queryParams =>
  queryParams
    ? `\n${TAB_STRING}${TAB_STRING}  queryParams: ${queryParams},`
    : "";

const parseExtraArgs = extraArgs =>
  toPairs(extraArgs).map(
    ([key, value]) => `\n${TAB_STRING}${TAB_STRING}${key}: "${value}"`
  );

const parseInlineStyle = styleString =>
  styleString
    .split(";")
    .map(rule => {
      const [key, value] = rule.split(":").map(s => s.trim());

      return key && value ? `${key}: "${value}"` : null;
    })
    .filter(Boolean)
    .join(", ");

const inlineEmbedCode = ({
  customization,
  id,
  embedScriptLink,
  extraArgs,
  inlineWrapperStyle,
  iframeStyle,
  queryParams,
}) => {
  const { elementSelector, dynamicHeight } = customization;

  const processedInlineWrapperStyle = dynamicHeight?.value
    ? removeHeightFromStyle(inlineWrapperStyle)
    : inlineWrapperStyle;

  return `
  import { useEffect } from "react";

  const App = () => {
    const ${embedFunctionName} = () => {
      ${embedScript(embedScriptLink)}
      embedScript.onload = () => {
        window.${camelCasedAppName}.embed({
          type: "inline",
          id: "${id}",
          organization: "${globalProps.organization.subdomain}",
          elementSelector: "#${elementSelector}",
          styles: "${iframeStyle}",${embedQueryParam(
    queryParams
  )}${embedEnv()}${parseExtraArgs(extraArgs)}
        });
      };

      document.body.appendChild(embedScript);
      ${cleanupFunction}
    };

    useEffect(() => ${embedFunctionName}(), []);

    return (
      <div
        id="inline-embed-container"
        style={{ ${parseInlineStyle(processedInlineWrapperStyle)} }}
      />
    );
  };`;
};

const floatingPopupEmbedCode = ({
  customization,
  id,
  embedScriptLink,
  extraArgs,
  queryParams,
}) => {
  const { btnTextColor, showIcon, btnText, btnPosition, btnColor, icon } =
    customization;

  return `
  import { useEffect } from "react";

  const App = () => {
    const ${embedFunctionName} = () => {
      ${embedScript(embedScriptLink)}
      embedScript.onload = () => {
        window.${camelCasedAppName}.embed({
          type: "floatingPopup",
          id: "${id}",
          organization: "${globalProps.organization.subdomain}",
          styles: {
            btnTextColor: "${btnTextColor}",
            btnPosition: "${btnPosition}",
            btnColor: "${btnColor}",
            btnText: "${btnText}",${
    icon
      ? `\n${TAB_STRING}${TAB_STRING}${TAB_STRING}showIcon: ${showIcon},`
      : ""
  }
          },${embedQueryParam(queryParams)}${embedEnv()}${parseExtraArgs(
    extraArgs
  )}
        });
      };

      document.body.appendChild(embedScript);
      ${cleanupFunction}
    };

    useEffect(() => ${embedFunctionName}(), []);

    return <div />;
  };`;
};

const elementPopupEmbedCode = ({
  customization,
  id,
  embedScriptLink,
  extraArgs,
  queryParams,
}) => {
  const { elementSelector } = customization;

  return `
  import { useEffect } from "react";

  const App = () => {
    const ${embedFunctionName} = () => {
      ${embedScript(embedScriptLink)}
      embedScript.onload = () => {
        window.${camelCasedAppName}.embed({
          type: "elementClick",
          id: "${id}",
          organization: "${globalProps.organization.subdomain}",
          elementSelector: "#${elementSelector}",${embedQueryParam(
    queryParams
  )}${embedEnv()}${parseExtraArgs(extraArgs)}
        });
      };

      document.body.appendChild(embedScript);
      ${cleanupFunction}
    };

    useEffect(() => ${embedFunctionName}(), []);

    return <button id="${elementSelector}">Click to open popup</button>;
  };`;
};

const iframeEmbedCode = ({ customization, id, queryParams }) => {
  const { height, width, iframeTitle, iframeUrl, dynamicHeight } =
    customization;

  const iframeSrc = buildIframeURL({ id, iframeUrl, queryParams });

  const heightAttribute = dynamicHeight?.value
    ? ""
    : `\n${TAB_STRING}${TAB_STRING}  height="${height}"`;

  return `
  const App = () => {
    return (
      <>
        {/* ${globalProps.appName} iframe embed code begins */}
        <iframe
          allowFullScreen${heightAttribute}
          width="${width}"
          frameBorder="0"
          title="${iframeTitle}"
          src="${iframeSrc}"
        ></iframe>
        {/* ${globalProps.appName} iframe embed code ends */}
      </>
    );
  };
`;
};

export const reactCodeGenerators = {
  inline: inlineEmbedCode,
  floatingPopup: floatingPopupEmbedCode,
  elementPopup: elementPopupEmbedCode,
  iframe: iframeEmbedCode,
};
